import tenantUtils from '@utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Algolia from '../../services/algolia';
import { SelectSearch } from '../common';

/**
 * TruLeads Simplification (86eye7076): a standalone Location filter that lists ALL
 * locations and is usable WITHOUT first picking a city (unlike the shared
 * CityLocationFilter, which is a city → location cascade). Selections are written under
 * the single subList key (e.g. `location_id`) and serialised to `f[location_id][]` by
 * the filter framework, exactly like the previous cascade — only the picker differs.
 */
const LocationFilter = (props) => {
  const { subList, filtersObject = {}, SPLIT_BY = ',', mode, setFiltersObject } = props;
  const locationSub = subList?.[0] || {};
  const key = locationSub.key;
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchInitialLocations();
    // Restore chips for locations already applied via the URL/filters.
    const selected = filtersObject[key];
    if (selected) fetchSelectedLocations(selected.split(SPLIT_BY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mergeLocations = (incoming) =>
    setLocations((prev) => [
      ...prev,
      ...incoming.filter((n) => !prev.some((p) => p.location_id === n.location_id)),
    ]);

  const fetchInitialLocations = async () => {
    setLoading(true);
    try {
      const queryObject = Algolia.getAllLocationsByName('');
      const response = await Algolia.getLocationsIndex().search('', queryObject);
      setLocations(response?.hits ?? []);
    } catch (error) {
      console.error('Failed to fetch locations:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  // Hydrate the labels of already-selected locations (by their objectID = location_id).
  const fetchSelectedLocations = async (ids) => {
    try {
      const { results } = await Algolia.getLocationsIndex().getObjects(ids);
      const hits = (results ?? []).filter(Boolean);
      if (hits.length) mergeLocations(hits);
    } catch (error) {
      console.error('Failed to fetch selected locations:', error?.message);
    }
  };

  const fetchLocations = async (text, callbackSuccess = () => {}, callback = () => {}) => {
    try {
      const queryObject = Algolia.getAllLocationsByName(text);
      const response = await Algolia.getLocationsIndex().search('', queryObject);
      if (response) {
        callbackSuccess(response);
        return response;
      }
      callback();
    } catch (error) {
      callback();
    }
  };

  const onSelectLocation = (value, option) => {
    const filters = { ...filtersObject };
    if (option?.length) {
      filters[key] = option.map((e) => e.location_id?.toString()).join(SPLIT_BY);
      setFiltersObject(filters);
    } else {
      delete filters[key];
      setFiltersObject(filters);
    }
  };

  return (
    <SelectSearch
      className="mb-24"
      value={mode === 'multiple' ? (filtersObject[key] ? filtersObject[key].split(SPLIT_BY) : []) : filtersObject[key]}
      onChange={(value, option) => onSelectLocation(value, option)}
      label={t('Location')}
      placeholder={t('Select Location')}
      fetchApi={fetchLocations}
      initialOptions={locations}
      payloadKey="hits"
      valueAsObj
      labelProps={{ muted: true }}
      getOptionValue={(e) => e.location_id}
      getOptionLabel={(e) => tenantUtils.getLocalisedString(e, 'title')}
      filterOption={false}
      allowClear
      mode={mode}
      loading={loading}
      {...locationSub}
    />
  );
};

export default LocationFilter;
